// src/pages/Donate.tsx
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { PageHero } from '@/components/PageHero';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
  Heart,
  Target,
  GraduationCap,
  Building2,
  Hand,
  CheckCircle,
  Users,
  TrendingUp,
  Award,
  DollarSign,
  Calendar,
  CreditCard,
  Wallet,
  Smartphone,
  Shield,
  Download,
  FileText,
  Info,
  AlertCircle,
  Star,
  Sparkles,
  ChevronRight,
  X,
  ArrowRight,
  Gift,
  BadgeCheck,
  Clock,
  Globe
} from 'lucide-react';
import {
  donationPrograms,
  impactStories,
  paymentMethods,
  donationStats,
  monthlyTiers,
  getProgressPercentage,
  type DonationProgram
} from '@/data/mock/donations';

const Donate = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const isRTL = i18n.language === 'ar';

  // Form state
  const [selectedProgram, setSelectedProgram] = useState<string>('');
  const [amount, setAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [donorInfo, setDonorInfo] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    anonymous: false,
    newsletter: true
  });
  const [showSuccess, setShowSuccess] = useState(false);

  // Get selected program details
  const currentProgram = useMemo(() => {
    return donationPrograms.find(p => p.id === selectedProgram);
  }, [selectedProgram]);

  // Icon mapping
  const iconMap: Record<string, any> = {
    Heart,
    Target,
    GraduationCap,
    Building2,
    Hand,
    Users,
    CreditCard,
    Wallet,
    Smartphone
  };

  const getIcon = (iconName: string) => {
    return iconMap[iconName] || Heart;
  };

  // Statistics
  const stats = [
    {
      icon: Users,
      value: `${donationStats.totalDonors.toLocaleString()}+`,
      label: { en: 'Generous Donors', ar: 'متبرعين كرماء' },
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: DollarSign,
      value: `$${(donationStats.totalRaised / 1000).toFixed(0)}K+`,
      label: { en: 'Total Raised', ar: 'إجمالي التبرعات' },
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Award,
      value: `${donationStats.athletesSupported}+`,
      label: { en: 'Athletes Supported', ar: 'رياضيين مدعومين' },
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Heart,
      value: `${donationStats.familiesHelped}+`,
      label: { en: 'Families Helped', ar: 'عائلات ساعدناها' },
      color: 'from-red-500 to-rose-500'
    }
  ];

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const finalAmount = customAmount || amount;

    if (!finalAmount || !selectedProgram || !paymentMethod) {
      toast({
        title: isRTL ? 'خطأ' : 'Error',
        description: isRTL ? 'الرجاء ملء جميع الحقول المطلوبة' : 'Please fill all required fields',
        variant: 'destructive',
      });
      return;
    }

    // In production: Integrate with payment gateway
    setShowSuccess(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Success Screen
  if (showSuccess) {
    return (
      <div className="min-h-screen">
        <PageHero
          title={t('donate.title')}
          subtitle={t('donate.subtitle')}
          breadcrumbs={[
            { label: t('common.home'), path: '/' },
            { label: t('nav.donate') }
          ]}
        />

        <section className="py-20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="max-w-3xl mx-auto"
            >
              <div className="bg-card border-2 border-primary/30 rounded-3xl p-12 text-center relative overflow-hidden">
                {/* Background decoration */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
                
                {/* Content */}
                <div className="relative z-10">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", duration: 0.6 }}
                    className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-primary/10 mb-8"
                  >
                    <CheckCircle className="h-16 w-16 text-primary" />
                  </motion.div>

                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-4xl font-bold mb-4 gradient-text"
                  >
                    {isRTL ? 'شكراً لكرمك!' : 'Thank You for Your Generosity!'}
                  </motion.h2>

                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto"
                  >
                    {isRTL
                      ? 'تبرعك يُحدث فرقاً حقيقياً في حياة رياضيينا الشباب. سنرسل لك تأكيداً عبر البريد الإلكتروني قريباً.'
                      : 'Your donation makes a real difference in the lives of our young athletes. We\'ll send you a confirmation email shortly.'
                    }
                  </motion.p>

                  {/* Donation Summary */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 border-2 border-primary/20 rounded-2xl p-8 mb-8"
                  >
                    <div className="grid md:grid-cols-2 gap-6 text-left">
                      <div>
                        <span className="text-sm text-muted-foreground block mb-2">
                          {isRTL ? 'المبلغ' : 'Amount'}
                        </span>
                        <span className="text-3xl font-bold text-primary">
                          ${customAmount || amount}
                        </span>
                        {isRecurring && (
                          <span className="text-sm text-muted-foreground block mt-1">
                            {isRTL ? '/شهرياً' : '/month'}
                          </span>
                        )}
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground block mb-2">
                          {isRTL ? 'البرنامج' : 'Program'}
                        </span>
                        <span className="font-bold">
                          {currentProgram?.name[isRTL ? 'ar' : 'en']}
                        </span>
                      </div>
                    </div>

                    {currentProgram && (
                      <div className="mt-6 pt-6 border-t border-border">
                        <div className="flex items-start gap-3 text-sm">
                          <Sparkles className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                          <p className="text-muted-foreground">
                            <strong className="text-foreground">{isRTL ? 'التأثير:' : 'Impact:'}</strong>{' '}
                            {currentProgram.impact[isRTL ? 'ar' : 'en']}
                          </p>
                        </div>
                      </div>
                    )}
                  </motion.div>

                  {/* Actions */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="flex flex-col sm:flex-row gap-4 justify-center"
                  >
                    <Button
                      onClick={() => window.location.reload()}
                      size="lg"
                      variant="outline"
                    >
                      {isRTL ? 'تبرع آخر' : 'Make Another Donation'}
                    </Button>
                    <Button
                      onClick={() => window.location.href = '/'}
                      size="lg"
                    >
                      {isRTL ? 'العودة للرئيسية' : 'Back to Home'}
                    </Button>
                  </motion.div>

                  {/* Social Share */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                    className="mt-8 pt-8 border-t border-border"
                  >
                    <p className="text-sm text-muted-foreground mb-4">
                      {isRTL ? 'شارك هذه الصفحة مع الآخرين' : 'Share this page with others'}
                    </p>
                    {/* Add social share buttons here */}
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  // Main Donate Page
  return (
    <div className="min-h-screen">
      {/* Enhanced Hero */}
      <PageHero
        title={isRTL ? 'تبرّع ودعم' : 'Donate & Support'}
        subtitle={isRTL
          ? 'تبرعك يُحدث فرقاً حقيقياً - ساعدنا في تغيير حياة الرياضيين الشباب'
          : 'Your donation makes a real difference - help us change young athletes\' lives'
        }
        breadcrumbs={[
          { label: t('common.home'), path: '/' },
          { label: t('nav.donate') }
        ]}
      />

      {/* Urgent Appeal Banner (if urgent programs exist) */}
      {donationPrograms.some(p => p.priority === 'urgent') && (
        <section className="py-6 bg-gradient-to-r from-red-500/10 via-orange-500/10 to-red-500/10 border-y border-red-500/20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center justify-center gap-3 text-center flex-wrap"
            >
              <AlertCircle className="h-6 w-6 text-red-500 animate-pulse" />
              <p className="text-sm font-semibold text-foreground">
                {isRTL
                  ? '🚨 نداء عاجل: نحتاج دعمك الآن أكثر من أي وقت مضى'
                  : '🚨 Urgent Appeal: We need your support now more than ever'
                }
              </p>
              <AlertCircle className="h-6 w-6 text-red-500 animate-pulse" />
            </motion.div>
          </div>
        </section>
      )}

      {/* Statistics */}
      <section className="py-20 bg-gradient-to-b from-background to-surface-1 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <TrendingUp className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'تأثيرنا معاً' : 'Our Impact Together'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'كل تبرع يصنع فرقاً' : 'Every Donation Makes a Difference'}
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ scale: 1.05, y: -8 }}
                className="p-8 rounded-3xl bg-card border-2 border-border hover:border-primary/30 shadow-xl hover:shadow-2xl transition-all duration-500 text-center"
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${stat.color} bg-opacity-10 mb-4`}>
                  <stat.icon className="h-8 w-8 text-primary" />
                </div>
                <div className="text-4xl font-bold gradient-text mb-2">{stat.value}</div>
                <div className="text-sm text-muted-foreground font-medium">
                  {stat.label[isRTL ? 'ar' : 'en']}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Stories */}
      <section className="py-20 bg-surface-1">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-yellow-500/10 border border-yellow-500/20 mb-8">
              <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
              <span className="text-sm font-semibold text-yellow-500">
                {isRTL ? 'قصص ملهمة' : 'Inspiring Stories'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'قصص التأثير' : 'Impact Stories'}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {isRTL
                ? 'شاهد كيف يُحدث تبرعك فرقاً حقيقياً في حياة الرياضيين الشباب'
                : 'See how your donation makes a real difference in young athletes\' lives'
              }
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {impactStories.map((story, idx) => (
              <motion.div
                key={story.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -8 }}
                className="bg-card border-2 border-border rounded-2xl overflow-hidden hover:border-primary/30 shadow-xl hover:shadow-2xl transition-all duration-500"
              >
                {story.image && (
                  <div className="aspect-video bg-muted overflow-hidden">
                    <img
                      src={story.image}
                      alt={story.title[isRTL ? 'ar' : 'en']}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                )}
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">
                    {story.title[isRTL ? 'ar' : 'en']}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                    {story.story[isRTL ? 'ar' : 'en']}
                  </p>
                  <div className="pt-4 border-t border-border">
                    <div className="flex items-center gap-2 text-sm text-primary font-semibold">
                      <Sparkles className="h-4 w-4" />
                      <span>{story.impact[isRTL ? 'ar' : 'en']}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Donation Form */}
      <section className="py-20 bg-gradient-to-b from-background to-surface-1">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'اختر كيف تريد المساعدة' : 'Choose How You Want to Help'}
            </h2>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Step 1: Select Program */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-card border-2 border-border rounded-3xl p-8"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">
                    1
                  </div>
                  <h3 className="text-2xl font-bold">
                    {isRTL ? 'اختر البرنامج' : 'Select Program'}
                  </h3>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {donationPrograms.map((program) => {
                    const Icon = getIcon(program.icon);
                    const progress = getProgressPercentage(program);
                    
                    return (
                      <button
                        key={program.id}
                        type="button"
                        onClick={() => {
                          setSelectedProgram(program.id);
                          setAmount('');
                          setCustomAmount('');
                        }}
                        className={`flex flex-col gap-4 p-6 rounded-2xl border-2 transition-all text-left relative overflow-hidden ${
                          selectedProgram === program.id
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        {/* Urgent Badge */}
                        {program.priority === 'urgent' && (
                          <div className="absolute top-3 right-3">
                            <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-red-500 text-white text-xs font-bold">
                              <AlertCircle className="h-3 w-3" />
                              {isRTL ? 'عاجل' : 'URGENT'}
                            </div>
                          </div>
                        )}

                        <div className="flex items-start gap-4">
                          <Icon className={`h-8 w-8 flex-shrink-0 ${selectedProgram === program.id ? 'text-primary' : 'text-muted-foreground'}`} />
                          <div className="flex-1 min-w-0">
                            <div className={`font-bold mb-2 ${selectedProgram === program.id ? 'text-primary' : ''}`}>
                              {program.name[isRTL ? 'ar' : 'en']}
                            </div>
                            <div className="text-sm text-muted-foreground line-clamp-2">
                              {program.description[isRTL ? 'ar' : 'en']}
                            </div>
                          </div>
                        </div>

                        {/* Progress Bar (if goal exists) */}
                        {program.goal && program.raised && (
                          <div className="space-y-2">
                            <div className="flex justify-between text-xs text-muted-foreground">
                              <span>${program.raised.toLocaleString()} {isRTL ? 'تم جمعها' : 'raised'}</span>
                              <span>${program.goal.toLocaleString()} {isRTL ? 'هدف' : 'goal'}</span>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <motion.div
                                initial={{ width: 0 }}
                                whileInView={{ width: `${progress}%` }}
                                viewport={{ once: true }}
                                transition={{ duration: 1, delay: 0.2 }}
                                className="h-full bg-gradient-to-r from-primary to-accent rounded-full"
                              />
                            </div>
                            <div className="text-xs font-semibold text-primary">
                              {progress}% {isRTL ? 'مكتمل' : 'complete'}
                            </div>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </motion.div>

              {/* Step 2: Choose Amount (shown only after program selection) */}
              <AnimatePresence>
                {selectedProgram && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="bg-card border-2 border-border rounded-3xl p-8"
                  >
                    <div className="flex items-center gap-3 mb-6">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">
                        2
                      </div>
                      <h3 className="text-2xl font-bold">
                        {isRTL ? 'اختر المبلغ' : 'Choose Amount'}
                      </h3>
                    </div>

                    {/* Recurring Toggle */}
                    <div className="mb-6 p-4 rounded-xl bg-primary/5 border border-primary/20">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isRecurring}
                          onChange={(e) => setIsRecurring(e.target.checked)}
                          className="w-5 h-5 rounded border-border text-primary focus:ring-primary"
                        />
                        <div className="flex-1">
                          <div className="font-semibold flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-primary" />
                            {isRTL ? 'جعل هذا تبرعاً شهرياً' : 'Make this a monthly donation'}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {isRTL 
                              ? 'ساعدنا في التخطيط بشكل أفضل مع تبرع شهري متكرر'
                              : 'Help us plan better with a recurring monthly donation'
                            }
                          </div>
                        </div>
                        {isRecurring && (
                          <BadgeCheck className="h-6 w-6 text-primary" />
                        )}
                      </label>
                    </div>

                    {/* Suggested Amounts */}
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
                      {currentProgram?.suggestedAmounts.map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => {
                            setAmount(preset.toString());
                            setCustomAmount('');
                          }}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            amount === preset.toString() && !customAmount
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border hover:border-primary/50'
                          }`}
                        >
                          <div className="text-2xl font-bold">${preset}</div>
                          {isRecurring && (
                            <div className="text-xs text-muted-foreground mt-1">/mo</div>
                          )}
                        </button>
                      ))}
                    </div>

                    {/* Custom Amount */}
                    <div>
                      <Label htmlFor="customAmount" className="text-base font-semibold mb-3 block">
                        {isRTL ? 'أو أدخل مبلغاً مخصصاً' : 'Or Enter Custom Amount'}
                      </Label>
                      <div className="relative">
                        <span className={`absolute top-1/2 -translate-y-1/2 text-muted-foreground text-lg ${isRTL ? 'right-4' : 'left-4'}`}>
                          $
                        </span>
                        <Input
                          id="customAmount"
                          type="number"
                          min={currentProgram?.minAmount || 1}
                          value={customAmount}
                          onChange={(e) => {
                            setCustomAmount(e.target.value);
                            setAmount('');
                          }}
                          className={`${isRTL ? 'pr-10' : 'pl-10'} h-14 text-lg`}
                          placeholder={isRTL ? 'أدخل المبلغ' : 'Enter amount'}
                        />
                        {currentProgram?.minAmount && (
                          <div className="text-xs text-muted-foreground mt-2">
                            {isRTL ? 'الحد الأدنى:' : 'Minimum:'} ${currentProgram.minAmount}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Impact Preview */}
                    {currentProgram && (amount || customAmount) && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-6 p-4 rounded-xl bg-primary/5 border border-primary/20"
                      >
                        <div className="flex items-start gap-3">
                          <Sparkles className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                          <div>
                            <div className="font-semibold text-primary mb-1">
                              {isRTL ? 'تأثير تبرعك:' : 'Your Impact:'}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {currentProgram.impact[isRTL ? 'ar' : 'en']}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Step 3: Payment Method */}
              <AnimatePresence>
                {selectedProgram && (amount || customAmount) && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="bg-card border-2 border-border rounded-3xl p-8"
                  >
                    <div className="flex items-center gap-3 mb-6">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">
                        3
                      </div>
                      <h3 className="text-2xl font-bold">
                        {isRTL ? 'طريقة الدفع' : 'Payment Method'}
                      </h3>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      {paymentMethods.map((method) => {
                        const Icon = getIcon(method.icon);
                        
                        return (
                          <button
                            key={method.id}
                            type="button"
                            onClick={() => setPaymentMethod(method.id)}
                            disabled={!method.available}
                            className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                              paymentMethod === method.id
                                ? 'border-primary bg-primary/10'
                                : 'border-border hover:border-primary/50'
                            } ${!method.available ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <Icon className={`h-8 w-8 ${paymentMethod === method.id ? 'text-primary' : 'text-muted-foreground'}`} />
                            <div className="flex-1">
                              <div className={`font-semibold ${paymentMethod === method.id ? 'text-primary' : ''}`}>
                                {method.name}
                              </div>
                              {method.description && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  {method.description[isRTL ? 'ar' : 'en']}
                                </div>
                              )}
                            </div>
                            {paymentMethod === method.id && (
                              <CheckCircle className="h-6 w-6 text-primary" />
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {/* Security Badge */}
                    <div className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                      <Shield className="h-4 w-4 text-green-500" />
                      <span>{isRTL ? '🔒 دفع آمن ومشفر' : '🔒 Secure & encrypted payment'}</span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Step 4: Donor Information */}
              <AnimatePresence>
                {selectedProgram && (amount || customAmount) && paymentMethod && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="bg-card border-2 border-border rounded-3xl p-8"
                  >
                    <div className="flex items-center gap-3 mb-6">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">
                        4
                      </div>
                      <h3 className="text-2xl font-bold">
                        {isRTL ? 'معلوماتك' : 'Your Information'}
                      </h3>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="donorName">
                          {isRTL ? 'الاسم' : 'Name'} <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="donorName"
                          required
                          value={donorInfo.name}
                          onChange={(e) => setDonorInfo({...donorInfo, name: e.target.value})}
                          className="mt-2"
                          placeholder={isRTL ? 'اسمك الكامل' : 'Your full name'}
                        />
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="donorEmail">
                            {isRTL ? 'البريد الإلكتروني' : 'Email'} <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="donorEmail"
                            type="email"
                            required
                            value={donorInfo.email}
                            onChange={(e) => setDonorInfo({...donorInfo, email: e.target.value})}
                            className="mt-2"
                            placeholder={isRTL ? 'بريدك الإلكتروني' : 'Your email'}
                          />
                        </div>

                        <div>
                          <Label htmlFor="donorPhone">
                            {isRTL ? 'الهاتف (اختياري)' : 'Phone (Optional)'}
                          </Label>
                          <Input
                            id="donorPhone"
                            type="tel"
                            value={donorInfo.phone}
                            onChange={(e) => setDonorInfo({...donorInfo, phone: e.target.value})}
                            className="mt-2"
                            placeholder={isRTL ? 'رقم هاتفك' : 'Your phone'}
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="donorMessage">
                          {isRTL ? 'رسالة (اختياري)' : 'Message (Optional)'}
                        </Label>
                        <Textarea
                          id="donorMessage"
                          rows={3}
                          value={donorInfo.message}
                          onChange={(e) => setDonorInfo({...donorInfo, message: e.target.value})}
                          className="mt-2"
                          placeholder={isRTL ? 'رسالتك أو تخصيص تبرعك...' : 'Your message or dedication...'}
                        />
                      </div>

                      {/* Checkboxes */}
                      <div className="space-y-3 pt-4">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={donorInfo.anonymous}
                            onChange={(e) => setDonorInfo({...donorInfo, anonymous: e.target.checked})}
                            className="w-5 h-5 rounded border-border text-primary focus:ring-primary mt-0.5"
                          />
                          <span className="text-sm">
                            {isRTL 
                              ? 'أرغب في أن يكون تبرعي مجهولاً'
                              : 'I wish to remain anonymous'
                            }
                          </span>
                        </label>

                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={donorInfo.newsletter}
                            onChange={(e) => setDonorInfo({...donorInfo, newsletter: e.target.checked})}
                            className="w-5 h-5 rounded border-border text-primary focus:ring-primary mt-0.5"
                          />
                          <span className="text-sm">
                            {isRTL 
                              ? 'أرغب في تلقي تحديثات حول تأثير تبرعي'
                              : 'I want to receive updates about my donation impact'
                            }
                          </span>
                        </label>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Submit Button */}
              <AnimatePresence>
                {selectedProgram && (amount || customAmount) && paymentMethod && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                  >
                    <Button
                      type="submit"
                      size="lg"
                      className="w-full btn-glow text-lg h-16"
                    >
                      <Heart className={`h-6 w-6 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                      {isRTL ? 'تبرّع الآن' : 'Donate Now'} ${customAmount || amount}{isRecurring ? '/mo' : ''}
                      <ArrowRight className={`h-6 w-6 ${isRTL ? 'mr-3' : 'ml-3'}`} />
                    </Button>

                    {/* Tax Deductible Info */}
                    {currentProgram?.taxDeductible && (
                      <div className="mt-4 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
                        <FileText className="h-4 w-4" />
                        <span>{isRTL ? '✓ قابل للخصم الضريبي' : '✓ Tax deductible'}</span>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </form>
          </div>
        </div>
      </section>

      {/* Monthly Giving Tiers */}
      <section className="py-20 bg-gradient-to-b from-surface-1 to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Gift className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'كن بطلاً شهرياً' : 'Become a Monthly Champion'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'مستويات التبرع الشهري' : 'Monthly Giving Tiers'}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {isRTL
                ? 'انضم إلى مجتمع المتبرعين الشهريين واحصل على مزايا حصرية'
                : 'Join our monthly donor community and get exclusive benefits'
              }
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {monthlyTiers.map((tier, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -8 }}
                className={`p-8 rounded-3xl border-2 ${
                  idx === 1 ? 'border-primary bg-primary/5' : 'border-border bg-card'
                } shadow-xl hover:shadow-2xl transition-all duration-500`}
              >
                {idx === 1 && (
                  <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-bold mb-4">
                    <Star className="h-3 w-3 fill-current" />
                    {isRTL ? 'الأكثر شيوعاً' : 'MOST POPULAR'}
                  </div>
                )}

                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-primary mb-2">${tier.amount}</div>
                  <div className="text-sm text-muted-foreground mb-4">{isRTL ? '/شهرياً' : '/month'}</div>
                  <div className="text-xl font-bold">{tier.name[isRTL ? 'ar' : 'en']}</div>
                </div>

                <ul className="space-y-3 mb-8">
                  {tier.benefits[isRTL ? 'ar' : 'en'].map((benefit, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className="w-full"
                  variant={idx === 1 ? 'default' : 'outline'}
                  onClick={() => {
                    setIsRecurring(true);
                    setAmount(tier.amount.toString());
                    setSelectedProgram('prog-006');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  {isRTL ? 'اختر هذا المستوى' : 'Choose This Tier'}
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Other Ways to Help */}
      <section className="py-20 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 relative overflow-hidden">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-primary/30 via-accent/20 to-transparent rounded-full blur-3xl"
        />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-bold mb-6">
              {isRTL ? 'طرق أخرى للمساعدة' : 'Other Ways to Help'}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={{ y: -8 }}
              className="p-8 rounded-3xl bg-card border-2 border-border shadow-xl text-center"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-6">
                <Target className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-4">
                {isRTL ? 'تبرع بالمعدات' : 'Donate Equipment'}
              </h3>
              <p className="text-muted-foreground mb-6">
                {isRTL
                  ? 'تبرع بمعدات رياضية جديدة أو مستعملة بحالة جيدة'
                  : 'Donate new or gently used sports equipment'
                }
              </p>
              <Button variant="outline" className="w-full">
                {isRTL ? 'تعرف على المزيد' : 'Learn More'}
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              whileHover={{ y: -8 }}
              className="p-8 rounded-3xl bg-card border-2 border-border shadow-xl text-center"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-6">
                <Users className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-4">
                {isRTL ? 'تطوّع' : 'Volunteer'}
              </h3>
              <p className="text-muted-foreground mb-6">
                {isRTL
                  ? 'شارك وقتك ومهاراتك لدعم رياضيينا الشباب'
                  : 'Share your time and skills to support our young athletes'
                }
              </p>
              <Button variant="outline" className="w-full">
                {isRTL ? 'انضم إلينا' : 'Join Us'}
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              whileHover={{ y: -8 }}
              className="p-8 rounded-3xl bg-card border-2 border-border shadow-xl text-center"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-6">
                <Globe className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-4">
                {isRTL ? 'انشر الكلمة' : 'Spread the Word'}
              </h3>
              <p className="text-muted-foreground mb-6">
                {isRTL
                  ? 'شارك قصتنا مع أصدقائك وعائلتك'
                  : 'Share our story with your friends and family'
                }
              </p>
              <Button variant="outline" className="w-full">
                {isRTL ? 'شارك' : 'Share'}
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-bold mb-6">
              {isRTL ? 'أسئلة شائعة' : 'Frequently Asked Questions'}
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {[
              {
                q: { en: 'Is my donation tax deductible?', ar: 'هل تبرعي قابل للخصم الضريبي؟' },
                a: { en: 'Yes, Champions Academy is a registered charity. All donations are tax deductible to the extent allowed by law.', ar: 'نعم، أكاديمية تشامبيونز منظمة خيرية مسجلة. جميع التبرعات قابلة للخصم الضريبي بالقدر المسموح به قانوناً.' }
              },
              {
                q: { en: 'How will my donation be used?', ar: 'كيف سيتم استخدام تبرعي؟' },
                a: { en: 'Your donation directly supports athlete training, equipment, scholarships, and facility maintenance. We provide detailed annual reports showing exactly how funds are used.', ar: 'يدعم تبرعك مباشرة تدريب الرياضيين والمعدات والمنح الدراسية وصيانة المرافق. نوفر تقارير سنوية مفصلة تُظهر بالضبط كيفية استخدام الأموال.' }
              },
              {
                q: { en: 'Can I donate in memory or honor of someone?', ar: 'هل يمكنني التبرع تذكاراً أو تكريماً لشخص ما؟' },
                a: { en: 'Absolutely! You can dedicate your donation in the message field, and we can send a notification to the honoree if you wish.', ar: 'بالتأكيد! يمكنك تخصيص تبرعك في حقل الرسالة، ويمكننا إرسال إشعار للشخص المُكرم إذا رغبت.' }
              }
            ].map((faq, idx) => (
              <motion.details
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="p-6 rounded-2xl bg-card border-2 border-border hover:border-primary/30 transition-all group"
              >
                <summary className="text-lg font-bold cursor-pointer list-none flex items-center justify-between">
                  <span>{faq.q[isRTL ? 'ar' : 'en']}</span>
                  <ChevronRight className="h-5 w-5 text-primary transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-4 text-muted-foreground leading-relaxed">
                  {faq.a[isRTL ? 'ar' : 'en']}
                </p>
              </motion.details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Donate;
